import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('polling'); // polling | paid | failed | expired
  const [orderInfo, setOrderInfo] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 8;

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }
    pollStatus();
  }, []);

  useEffect(() => {
    if (status === 'polling' && attempts > 0 && attempts < maxAttempts) {
      const timer = setTimeout(pollStatus, 2500);
      return () => clearTimeout(timer);
    }
  }, [attempts]);

  const pollStatus = async () => {
    try {
      const res = await axios.get(`${API}/checkout/status/${sessionId}`);
      const data = res.data;
      setOrderInfo(data);

      if (data.payment_status === 'paid') {
        setStatus('paid');
      } else if (data.status === 'expired') {
        setStatus('expired');
      } else {
        setAttempts(prev => {
          if (prev + 1 >= maxAttempts) {
            setStatus('failed');
          }
          return prev + 1;
        });
      }
    } catch (err) {
      console.error('Status poll error:', err);
      setAttempts(prev => {
        if (prev + 1 >= maxAttempts) {
          setStatus('failed');
        }
        return prev + 1;
      });
    }
  };

  if (status === 'polling') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12 px-4" data-testid="payment-polling">
        <Card className="w-full max-w-md border-2">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-purple-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">Confirming Payment...</h2>
            <p className="text-slate-500 text-sm">Please wait while we verify your payment.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'paid') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12 px-4" data-testid="payment-success">
        <Card className="w-full max-w-lg border-2">
          <CardContent className="p-8 text-center">
            <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800 mb-2">Payment Successful!</h1>
            <p className="text-slate-600 mb-6">
              Your payment has been processed. Thank you for your order.
            </p>

            {orderInfo && (
              <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left text-sm">
                {orderInfo.order_id && (
                  <div className="flex justify-between mb-2">
                    <span className="text-slate-500">Order ID:</span>
                    <span className="font-semibold text-slate-800">{orderInfo.order_id}</span>
                  </div>
                )}
                <div className="flex justify-between mb-2">
                  <span className="text-slate-500">Amount:</span>
                  <span className="font-semibold text-green-600">
                    ${(orderInfo.amount_total / 100).toFixed(2)} {orderInfo.currency?.toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Status:</span>
                  <span className="font-semibold text-green-600 capitalize">{orderInfo.payment_status}</span>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/" className="flex-1">
                <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white" data-testid="continue-shopping-btn">
                  Continue Shopping
                </Button>
              </Link>
              <Link to="/track-order" className="flex-1">
                <Button variant="outline" className="w-full" data-testid="track-order-btn">
                  <Package className="mr-2 h-4 w-4" />
                  Track Order
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Failed or expired
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12 px-4" data-testid="payment-failed">
      <Card className="w-full max-w-md border-2">
        <CardContent className="p-8 text-center">
          <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">
            {status === 'expired' ? 'Payment Session Expired' : 'Payment Verification Failed'}
          </h2>
          <p className="text-slate-600 mb-6 text-sm">
            {status === 'expired'
              ? 'Your payment session has expired. Please try again.'
              : 'We could not verify your payment. If you were charged, please contact support.'}
          </p>
          <div className="flex flex-col gap-3">
            <Link to="/checkout">
              <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white">
                Try Again
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="w-full">
                Contact Support
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
